/** Русская форма множественного числа: 1 участник, 2 участника, 5 участников. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;

  const mod10 = n % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export const participantsWord = (n: number) => plural(n, 'участник', 'участника', 'участников');

/** Полная ссылка-приглашение на встречу. */
export const meetingUrl = (code: string) => `${window.location.origin}/j/${code}`;

/** Длительность встречи в виде 07:31 или 1:07:31. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

export const formatTime = (at: number) =>
  new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

/** «Сегодня в 14:30», «Завтра в 09:00», «12 августа в 15:00». */
export function formatWhen(at: number | null): string {
  if (at === null) return 'Начнётся сразу';

  const date = new Date(at);
  const time = formatTime(at);

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(date) - startOfDay(new Date())) / 86_400_000);

  if (days === 0) return `Сегодня в ${time}`;
  if (days === 1) return `Завтра в ${time}`;
  if (days === -1) return `Вчера в ${time}`;

  const day = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  return `${day} в ${time}`;
}

/** Обратный отсчёт словами: «через 2 ч 05 мин». */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  if (days > 0) return `${days} ${plural(days, 'день', 'дня', 'дней')} ${hours} ч`;
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}

/** Инициалы для аватара-заглушки, когда камера выключена. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Подписи плитки: на экране и в файле записи они разные.
 *
 * ── Почему разные ───────────────────────────────────────────────────
 *
 * На экране у плитки демонстрации написано «Имя · демонстрация экрана».
 * Там это нужно: в ленте среди лиц надо понимать, что это чей-то экран,
 * а не человек. В записи та же приписка только мешает — что показывают,
 * и так видно, а надпись висит поверх слайда весь ролик.
 *
 * Правило вынесено сюда, а не оставлено в вёрстке, потому что от него
 * зависит `recorder.ts`. Пока оно жило в JSX, любая правка подписи тихо
 * меняла бы и содержимое файлов записи.
 */
export function tileCaptions(
  name: string,
  { isLocal, isScreen }: { isLocal: boolean; isScreen: boolean },
): { screen: string; record: string } {
  // «· вы» относится к человеку, а не к его экрану: на плитке
  // демонстрации она сбивает с толку.
  const own = isLocal && !isScreen ? ' · вы' : '';
  const record = `${name}${own}`;
  return { screen: isScreen ? `${record} · демонстрация экрана` : record, record };
}
